/**
 * Enalysis (Detechtion) REST API client helpers.
 *
 * Base URL comes from `import.meta.env.VITE_ENALYSIS_API_URL`. Requests use
 * `withCredentials: true`. Company-scoped calls set the Detechtion user cookie
 * via {@link setDetechtionUserCompanyCookie} before each GET.
 *
 * Trend endpoints request `ToUtc: true` so `reportDate` and related fields are
 * returned as UTC ISO-8601 instants (e.g. trailing `Z`).
 */
import axios from "axios";
import Cookies from "js-cookie";
import {
  ENALYSIS_TRENDS_KEYS_MAX_CHUNK_CHARS,
  ENALYSIS_TRENDS_KEYS_MAX_PER_REQUEST,
} from "@/constants/enalysis-trends-constants.js";

const DETECHTION_USER_COOKIE = "DetechtionUser";
const COOKIE_DOMAIN = "detechtion.com";

/** @returns {{ userName: string, fullName: string }} */
function readDetechtionUserNames() {
  const raw = Cookies.get(DETECHTION_USER_COOKIE);
  if (!raw || typeof raw !== "string") {
    return { userName: "", fullName: "" };
  }
  const s = raw.replace(/;\s*secure;?$/i, "").trim();
  const out = { userName: "", fullName: "" };
  for (const segment of s.split("&")) {
    const eq = segment.indexOf("=");
    if (eq === -1) continue;
    const key = segment.slice(0, eq);
    const val = segment.slice(eq + 1);
    if (key === "UserName") out.userName = val;
    if (key === "FullName") out.fullName = val;
  }
  return out;
}

/**
 * Writes `DetechtionUser` cookie with `SelectedCompanyID` for subsequent Enalysis requests.
 * @param {string|number|null|undefined} selectedCompany - Company id selected in OneView
 */
export function setDetechtionUserCompanyCookie(selectedCompany) {
  if (selectedCompany == null) return;

  const fromCookie = readDetechtionUserNames();
  const userName =
    fromCookie.userName || Cookies.get("DetechtionAuth.Username") || "";
  const fullName = fromCookie.fullName || "";

  document.cookie = `${DETECHTION_USER_COOKIE}=""; expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=${COOKIE_DOMAIN};path=/`;

  const secureMiddle =
    import.meta.env.VITE_SECURE_COOKIES === "true" ? "secure;" : "";
  let inner = `UserName=${userName}&FullName=${fullName}&SelectedCompanyID=${selectedCompany};${secureMiddle}`;
  document.cookie = `${DETECHTION_USER_COOKIE}=${inner}; domain=${COOKIE_DOMAIN}; path=/`;
}

/**
 * Absolute URL to the Enalysis web app Vital Signs page for an asset.
 * @param {string|number} enalysisCompressorNum - Detechtion / Enalysis compressor number
 * @param {string|number} enalysisCompanyId - Enalysis company id
 * @returns {string}
 */
export function getEnalysisVitalSignsViewUrl(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  const base = import.meta.env.VITE_ENALYSIS_WEBSITE_URL;
  return `${base}/asset/${encodeURIComponent(enalysisCompressorNum)}/vitalsigns/?companyID=${encodeURIComponent(enalysisCompanyId)}`;
}

const defaultAxiosOpts = {
  baseURL: import.meta.env.VITE_ENALYSIS_API_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true,
};

const enalysisAxiosInstance = axios.create(defaultAxiosOpts);

enalysisAxiosInstance.interceptors.request.use((config) => {
  config.baseURL = import.meta.env.VITE_ENALYSIS_API_URL;
  return config;
});

/**
 * GET helper: sets company cookie, then returns `response.data`.
 * @param {string} path - Path relative to Enalysis API base URL
 * @param {string|number} enalysisCompanyId - Company id for cookie scope
 * @returns {Promise<unknown>}
 */
async function getWithCompanyCookie(path, enalysisCompanyId) {
  setDetechtionUserCompanyCookie(enalysisCompanyId);
  const response = await enalysisAxiosInstance.get(path);
  return response.data;
}

/**
 * Vital signs payload for the asset unit view.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getVitalSigns(enalysisCompressorNum, enalysisCompanyId) {
  return getWithCompanyCookie(
    `/AssetUnitView/VitalSigns/${encodeURIComponent(enalysisCompressorNum)}/${encodeURIComponent(enalysisCompanyId)}`,
    enalysisCompanyId
  );
}

/**
 * Full Enalysis report for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisReport(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/reports/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Enalysis module list for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisModules(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/modules/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Enalysis frame definitions for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisFrames(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/frames/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Aerial cooler configuration for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisAerialCoolers(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/aerialcoolers/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Cylinder data for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisCylinders(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/cylinders/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Design conditions for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisDesignConditions(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/designconditions/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Clearance data for a compressor.
 * @param {string|number} enalysisCompressorNum
 * @param {string|number} enalysisCompanyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisClearances(
  enalysisCompressorNum,
  enalysisCompanyId
) {
  return getWithCompanyCookie(
    `/clearances/${encodeURIComponent(enalysisCompressorNum)}`,
    enalysisCompanyId
  );
}

/**
 * Warning flags from the report service.
 * @param {string|number} detechtionCompressorNumber
 * @param {string|number} companyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisWarningFlags(
  detechtionCompressorNumber,
  companyId
) {
  return getWithCompanyCookie(
    `/reports/WarningFlags/${encodeURIComponent(detechtionCompressorNumber)}`,
    companyId
  );
}

/**
 * Allowed trend identifiers and labels for an asset (`/Trends/AssetTrendsList/...`).
 * @param {string|number} detechtionCompressorNumber
 * @param {string|number} companyId
 * @returns {Promise<unknown>}
 */
export async function getEnalysisAssetTrendsList(
  detechtionCompressorNumber,
  companyId
) {
  return getWithCompanyCookie(
    `/Trends/AssetTrendsList/${encodeURIComponent(detechtionCompressorNumber)}/${encodeURIComponent(companyId)}`,
    companyId
  );
}

/**
 * Splits a comma-separated trend key list into trimmed unique keys (preserves order).
 * @param {string} keysCommaSeparated
 * @returns {string[]}
 */
function parseEnalysisTrendKeysCsv(keysCommaSeparated) {
  const raw = String(keysCommaSeparated ?? "").trim();
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const part of raw.split(",")) {
    const k = part.trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/**
 * Splits keys into URL-safe chunks limited by count and approximate joined length.
 * @param {string[]} keys
 * @param {number} maxPerChunk - Max keys per HTTP request
 * @param {number} maxChunkChars - Max length of comma-joined `Keys` query param
 * @returns {string[][]}
 */
function chunkEnalysisTrendKeys(keys, maxPerChunk, maxChunkChars) {
  if (keys.length === 0) return [];
  const chunks = [];
  let current = [];
  let joinedLen = 0;

  for (const k of keys) {
    const addLen = current.length === 0 ? k.length : 1 + k.length;
    const wouldExceedChars = joinedLen + addLen > maxChunkChars;
    const wouldExceedCount = current.length >= maxPerChunk;
    if (current.length > 0 && (wouldExceedChars || wouldExceedCount)) {
      chunks.push(current);
      current = [];
      joinedLen = 0;
    }
    current.push(k);
    joinedLen += current.length === 1 ? k.length : 1 + k.length;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

/**
 * Normalizes a `/Trends` response body to an array of series objects.
 * Matches `seriesListFromTrendsData` in `enalysis-trends-tag-utils` (array root vs `.data`).
 * @param {unknown} trendsData
 * @returns {object[]}
 */
function extractTrendSeriesArray(trendsData) {
  if (Array.isArray(trendsData)) {
    return trendsData.filter((x) => x != null && typeof x === "object");
  }
  const inner = trendsData?.data;
  if (Array.isArray(inner)) {
    return inner.filter((x) => x != null && typeof x === "object");
  }
  return [];
}

/**
 * Single GET `/Trends/...` with optional `Keys` and `ToUtc: true`.
 * @param {string} path - Full path under base URL
 * @param {string} keysCsv - Comma-separated trend keys, or empty string
 * @returns {Promise<unknown>}
 */
async function getEnalysisTrendsDataSingleRequest(path, keysCsv) {
  const keys = String(keysCsv ?? "").trim();
  const response = await enalysisAxiosInstance.get(path, {
    params: {
      ToUtc: true,
      ...(keys ? { Keys: keys } : {}),
    },
  });
  return response.data;
}

/**
 * Trend time-series for a compressor over `rangeMonths`, optionally filtered by keys.
 * Splits large key lists into parallel requests and merges series into `{ data: [...] }`.
 *
 * @param {string|number} detechtionCompressorNumber
 * @param {string|number} rangeMonths - Lookback window passed as path segment
 * @param {string|number} companyId
 * @param {string} keysCommaSeparated - Comma-separated trend identifiers (may be empty)
 * @returns {Promise<unknown>} Raw API body, or `{ data: mergedSeries[] }` when multiple key chunks are used
 */
export async function getEnalysisTrendsData(
  detechtionCompressorNumber,
  rangeMonths,
  companyId,
  keysCommaSeparated
) {
  setDetechtionUserCompanyCookie(companyId);
  const path = `/Trends/${encodeURIComponent(detechtionCompressorNumber)}/${encodeURIComponent(rangeMonths)}/${encodeURIComponent(companyId)}`;
  const keyList = parseEnalysisTrendKeysCsv(keysCommaSeparated);

  if (keyList.length === 0) {
    return getEnalysisTrendsDataSingleRequest(path, "");
  }

  const chunks = chunkEnalysisTrendKeys(
    keyList,
    ENALYSIS_TRENDS_KEYS_MAX_PER_REQUEST,
    ENALYSIS_TRENDS_KEYS_MAX_CHUNK_CHARS
  );

  if (chunks.length === 1) {
    return getEnalysisTrendsDataSingleRequest(path, chunks[0].join(","));
  }

  const results = await Promise.all(
    chunks.map((c) =>
      getEnalysisTrendsDataSingleRequest(path, c.join(","))
    )
  );
  const merged = results.flatMap(extractTrendSeriesArray);
  return { data: merged };
}

/** Preconfigured Axios instance for Enalysis (`VITE_ENALYSIS_API_URL`, credentials, JSON). */
export default enalysisAxiosInstance;
